import React, { useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { formatReadableAmount } from "../../Common/FormatFunctions/FormatAmount";
import { CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API;

const CreditnoteSummary = ({ fromDate, toDate, companyCode, yearCode }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isDataFetched, setIsDataFetched] = useState(false);

    const fetchCreditnoteSummary = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`${API_URL}/Creditnote-summary`, {
                params: {
                    from_date: fromDate,
                    to_date: toDate,
                    Company_Code: companyCode,
                    Year_Code: yearCode,
                },
            });
            if (response.data.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Data Not Found!',
                    text: 'No Credit Note data found for the selected date range.',
                });
                return;
            }
            setData(response.data);
            setIsDataFetched(true);
            openReportInNewTab(response.data);
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const openReportInNewTab = (data) => {
        const newWindow = window.open('', '_blank');
        if (!newWindow) return;

        const columns = [
            'SR_No',
            'CreditNote_No',
            'PartyGSTNo',
            'PartyCode',
            'PartyName',
            'PartyStateCode',
            'Date',
            'Quintal',
            'Rate',
            'TaxableAmount',
            'CGST',
            'SGST',
            'IGST',
            'Final_Amount',
        ];

        const totals = calculateTotals(data);

        const exportXlsxScript = `
        function exportToXlsx() {
            const data = ${JSON.stringify(data)};
            const columnOrder = ['SR_No', 'CreditNote_No', 'PartyGSTNo', 'PartyCode', 'PartyName', 'PartyStateCode', 'Date', 'Quintal', 'Rate', 'TaxableAmount', 'CGST', 'SGST', 'IGST', 'Final_Amount'];
            const formattedData = data.map(row => {
                return {
                    ...row,
                    TaxableAmount: parseFloat(row.TaxableAmount) || 0,
                    CGST: parseFloat(row.CGST) || 0,
                    SGST: parseFloat(row.SGST) || 0,
                    IGST: parseFloat(row.IGST) || 0,
                    Final_Amount: parseFloat(row.Final_Amount) || 0,
                };
            });
    
            const ws = XLSX.utils.json_to_sheet(formattedData, { header: columnOrder, skipHeader: false });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'CreditnoteSummary');
            XLSX.writeFile(wb, 'CreditnoteSummary.xlsx');
        }
    `;

        newWindow.document.write(`
            <html>
                <head>
                    <title>Credit Note Summary Report</title>
                    <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
                    <script>${exportXlsxScript}</script>
                   <style>
                    body { font-family: Arial, sans-serif; padding: 20px; text-align: center; }
                    h2 { text-align: center; margin-top: 0; }
                    .table-container {
                        max-height: 500px;
                        overflow-y: auto;
                        margin: 20px auto;
                        width: 90%;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    th, td {
                        border: 1px solid #ddd;
                        padding: 6px 8px;
                        text-align: left;
                        white-space: nowrap;
                    }
                    th {
                        background-color:rgb(206, 200, 243);
                        position: sticky;
                        top: 0;
                        z-index: 2;
                        font-weight: bold;
                        font-size: 20px;
                        height: 50px;
                        text-align: center;
                    }
                    .export-btn {
                        padding: 10px 20px;
                        font-size: 16px;
                        background-color: green;
                        color: white;
                        border: none;
                        cursor: pointer;
                        margin-bottom: 20px;
                        margin-top: 20px;
                    }
                    .total-row {
                        background-color: yellow;
                        font-weight: bold;
                    }
                </style>
                </head>
                <body>
                    <h2>Credit Note Summary Report</h2>
                    <button class="export-btn" onclick="window.exportToXlsx()">Export to XLSX</button>
                    <table>
                        <thead>
                            <tr>
                                ${columns.map((column) => `<th>${column}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${data.map(row => {
            return `  
                                    <tr>
                                        ${columns.map(column => {
                if (['TaxableAmount', 'CGST', 'SGST', 'IGST', 'Final_Amount'].includes(column)) {
                    return `<td style="text-align: right;">${formatReadableAmount(row[column] || 0)}</td>`;
                } else {
                    return `<td>${row[column] || ''}</td>`;
                }
            }).join('')}
                                    </tr>
                                `;
        }).join('')}
                        </tbody>
                        <tfoot>
                            <tr class="total-row">
                                <td colspan="7" style="font-weight: bold; background-color: yellow;"></td>
                                <td>Total</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.Quintal.toFixed(2))}</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.TaxableAmount.toFixed(2))}</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.CGST.toFixed(2))}</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.SGST.toFixed(2))}</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.IGST.toFixed(2))}</td>
                                <td style="text-align: right;">${formatReadableAmount(totals.Final_Amount.toFixed(2))}</td>
                            </tr>
                        </tfoot>
                    </table>
                </body>
            </html>
        `);

        newWindow.document.close();
    };

    const calculateTotals = (data) => {
        let totals = {
            Quintal: 0,
            TaxableAmount: 0,
            CGST: 0,
            SGST: 0,
            IGST: 0,
            Final_Amount: 0,
        };

        data.forEach(row => {
            totals.Quintal += parseFloat(row.Quintal || 0);
            totals.TaxableAmount += parseFloat(row.TaxableAmount || 0);
            totals.CGST += parseFloat(row.CGST || 0);
            totals.SGST += parseFloat(row.SGST || 0);
            totals.IGST += parseFloat(row.IGST || 0);
            totals.Final_Amount += parseFloat(row.Final_Amount || 0);
        });
        return totals;
    };

    return (
        <div className="d-flex flex-column align-items-center" style={{ marginTop: '5px' }}>
            <button
                className="btn btn-primary"
                onClick={fetchCreditnoteSummary}
                disabled={loading}
                style={{
                    width: '20%',
                    height: '60px',
                }}
            >
                {loading ? <CircularProgress size={24} /> : 'Credit Note Summary'}
            </button>
            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );
};

export default CreditnoteSummary;

